import React, { useState, useEffect } from 'react';
import { DataTable } from '../../components/shared/DataTable';
import { StatusBadge } from '../../components/shared/StatusBadge';
import { fetchCaseDeletions } from '../../api';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { format } from 'date-fns';

export default function RemovedCases() {
  const [removedCases, setRemovedCases] = useState([]);

  useEffect(() => {
    async function load() {
      try {
        const data = await fetchCaseDeletions('removed');
        if (Array.isArray(data)) {
          const rejected = data.map(c => ({
            ...c,
            client: c.client || { name: c.clientName || 'Client', logo: '' },
            product: c.product || { name: c.productName || 'Verification' },
            rejectionReason: c.delete_remark || c.delete_reason || 'Removed by administrator.',
            removedDate: c.updatedAt || new Date().toISOString()
          }));
          setRemovedCases(rejected);
        }
      } catch (e) {
        console.error("Failed to load removed cases:", e);
        setRemovedCases([]);
      }
    }
    load();
  }, []);

  const columns = [
    {
      key: 'refNumber',
      header: 'Case ID',
      sortable: true,
      render: (row) => (
        <div>
          <p className="font-medium text-blue-600">{row.refNumber}</p>
          <p className="text-xs text-slate-500">{row.product.name}</p>
        </div>
      ),
    },
    {
      key: 'client',
      header: 'Client',
      render: (row) => (
        <div className="flex items-center gap-2">
          <Avatar className="w-7 h-7">
            <AvatarImage src={row.client.logo} />
            <AvatarFallback className="text-xs">{row.client.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <span className="text-sm">{row.client.name}</span>
        </div>
      ),
    },
    {
      key: 'applicantName',
      header: 'Applicant',
      render: (row) => <span className="font-medium text-slate-900">{row.applicantName}</span>,
    },
    {
      key: 'rejectionReason',
      header: 'Rejection Reason',
      render: (row) => (
        <span className="text-sm text-red-500 font-medium max-w-[200px] truncate block" title={row.rejectionReason}>
          {row.rejectionReason}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Tracking Status',
      render: (row) => (
        <StatusBadge status="danger" label="Rejected / Removed" />
      ),
    },
    {
      key: 'removedDate',
      header: 'Removed On',
      sortable: true,
      render: (row) => (
        <span className="text-sm text-slate-500">
          {format(new Date(row.removedDate), 'MMM dd, yyyy')}
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="removed-cases-page">
      <div className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur-md pb-4 pt-2 mb-6 border-b border-slate-200">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Removed Cases</h1>
        <p className="text-sm text-slate-500 mt-1">Track and review all rejected cases and their reasons.</p>
      </div>

      <div className="crm-card bg-white p-4 rounded-xl shadow-sm border border-slate-200">
        <DataTable
          columns={columns}
          data={removedCases}
          searchPlaceholder="Search removed cases..."
          selectable={false}
          pageSize={10}
        />
      </div>
    </div>
  );
}
